import { useAppSelector } from '@/store';
import { authStore } from '@/store/auth';
import { ServiceGroup, URL_CATEGORIES } from '@/types/serviceGroup';
import { UserSession } from '@/types/userSession';
import { OIDC_MIGRATED_SERVICES } from '@/constants';

export const useExtractLinksFromService = (service: ServiceGroup) => {
  const userSession = useAppSelector(authStore.selectors.userSessionSelector);
  const isManager = useAppSelector(authStore.selectors.isManagerSelector);
  return extractLinksFromService(service, userSession, isManager);
};

const AUTH_URL_REGEX = /https?:\/\/(stg\d?\.)?auth\.globis\.co\.jp/;
const EMBA_URL_REGEX =
  /https:\/\/(stg-)?learning\.digitalglobis\.jp\/eLM\/admin\/dglp_check\.cfm/;
// SAML認証URLの正規表現
// マッチする条件:
//   - プロトコル: httpsのみ
//   - ドメイン: auth.globis.co.jp または stg.auth.globis.co.jp または stg{数字}.auth.globis.co.jp
//   - パス: /auth/saml/idp で始まる
const SAML_AUTH_URL_REGEX =
  /https:\/\/(stg\d?\.)?auth\.globis\.co\.jp\/auth\/saml\/idp/;

// FIXME: API側に処理持っててほしい
const extractLinksFromService = (
  service: ServiceGroup,
  session: UserSession,
  isManager: boolean,
) => {
  const attachedLinks = service.urls
    .filter((link) => {
      if (isManager) {
        return true;
      }
      return [
        URL_CATEGORIES.LANDINGPAGE,
        URL_CATEGORIES.ADMIN,
        URL_CATEGORIES.OTHERSSO,
      ].includes(link.category);
    })
    .map((link) => {
      const { url, category } = link;
      const { gloplaId, token } = session;
      const delimiter = url.indexOf('?') === -1 ? '?' : '&';
      // OIDC移行済みサービス設定を取得
      const oidcServiceCategory =
        OIDC_MIGRATED_SERVICES[
          service.id as keyof typeof OIDC_MIGRATED_SERVICES
        ];
      /**
       * isOidcMigratedServiceの判定
       * - サービスIDとcategoryが一致
       * - SAML認証URLを除外
       */
      const isOidcMigratedService =
        oidcServiceCategory &&
        category === oidcServiceCategory &&
        !SAML_AUTH_URL_REGEX.test(url);
      const isEmbaUrl = EMBA_URL_REGEX.test(url);
      const isGloplaIdIncluded = link.url.includes('glopla_id');

      if (
        !url.startsWith('http') &&
        category === URL_CATEGORIES.SUBSCRIPTIONLIST
      ) {
        link.url = `${url}${delimiter}service_group_id=${service.id}`;
      }

      /**
       * OIDC移行済みサービスへのglopla_id付与
       *
       * OIDC_MIGRATED_SERVICESに定義されたサービスの対象categoryに
       * glopla_idパラメータを付与
       * （eMBA特定URLとSAML認証URLは除外）
       */
      if (isOidcMigratedService && !isEmbaUrl && !isGloplaIdIncluded) {
        link.url = `${url}${delimiter}glopla_id=${gloplaId}`;
      }

      if (AUTH_URL_REGEX.test(url)) {
        link.url = `${url}${delimiter}at=${gloplaId}:${token}`;
      }
      return link;
    });

  return {
    admin: attachedLinks.filter(
      (link) => link.category === URL_CATEGORIES.ADMIN,
    ),
    subscribe:
      attachedLinks.find(
        (link) => link.category === URL_CATEGORIES.SUBSCRIBE,
      ) || null,
    subscriptionList:
      attachedLinks.find(
        (link) => link.category === URL_CATEGORIES.SUBSCRIPTIONLIST,
      ) || null,
    landingPage:
      attachedLinks.find(
        (link) => link.category === URL_CATEGORIES.LANDINGPAGE,
      ) || null,
    otherSso:
      attachedLinks.find((link) => link.category === URL_CATEGORIES.OTHERSSO) ||
      null,
  };
};
